import type { Store } from '@reduxjs/toolkit';

import { reportErrorToSentry } from '../setup/sentry';
import { langSlice } from './langSlice';
import { DEFAULT_LOCALE, getSupportedLocale as defaultGetSupportedLocale } from './lang';
import { trace } from '../setup/trace';
import { getMomentLocaleKey, importMomentLocale } from './momentLocales';

const importDisplayMessages = async (locale: string) =>
    (await import(`../../features/translations/${locale}.json`)).default;

export const configureFetchDisplayMessages =
    (store: Store, fetchDisplayMessages = importDisplayMessages, getSupportedLocale = defaultGetSupportedLocale) =>
    async (locale: string) => {
        if (!locale) {
            console.warn('No "locale" supplied when fetching display messages!');
            return Promise.reject();
        }

        const supportedLocale = getSupportedLocale(locale);

        const currentLocale = store.getState().lang?.displayLocale;
        if (currentLocale === supportedLocale) {
            console.warn(`Locale ${locale} is already configured`);
            return;
        }
        try {
            const displayMessages = await fetchDisplayMessages(supportedLocale);
            trace(`Display messages fetched for "${supportedLocale}"`);
            store.dispatch(langSlice.actions.displayMessagesFetched({ locale: supportedLocale, displayMessages }));
        } catch (error: unknown) {
            console.error(`Display messages for "${supportedLocale}" could not be fetched.`, error);
            reportErrorToSentry(error);
            store.dispatch(langSlice.actions.localeChanged(DEFAULT_LOCALE));
        }

        try {
            const momentLocale = getMomentLocaleKey(supportedLocale);
            await importMomentLocale(momentLocale);
        } catch (error: unknown) {
            console.error(`Moment locale for "${supportedLocale}" could not be imported.`, error);
            reportErrorToSentry(error);
        }
    };
